import { TriggerProps } from '@atlaskit/popup';
import useCommentContext from '@databases/comments/hooks/useCommentContext';
import { useContextualMenu } from '@databases/contextual-menu';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectValueRange,
  selectValueSingle,
  setFirstSelectedValue,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsValueSelected,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import { useScope } from '@databases/scope';
import { blurActiveElement } from '@databases/utils/database/utils';
import { getValueId } from '@databases/utils/utils';
import React, { useCallback } from 'react';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import SelectValueMenu from '../contextual-menu/SelectValueMenu';
import TableLayoutCell, { TableLayoutCellProps } from '../table-layout/TableLayoutCell';

type TableLayoutCellWithMenuProps = TableLayoutCellProps;

const TableLayoutCellWithMenu = React.memo(function TableLayoutCellWithMenu({
  contentId,
  entryId,
  fieldId,
  isContentEditable,
  canEditContent,
  canEditFields,
  isSelectionDisabled,
  currentViewId,
  ...tableLayoutCellProps
}: TableLayoutCellWithMenuProps) {
  const scope = useScope();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const { openThread } = useCommentContext();

  const { toggleOpenMenu, closeMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
  });

  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      const startedRenderingAt = performance.now();
      closeMenu();

      dispatch(setSoftSelection({ value: false }));
      if (
        selectSelectedValuesCount(store.getState(), {
          contentId,
        }) === 0
      ) {
        dispatch(setFirstSelectedValue({ value: { entryId, fieldId } }));
      }
      if (options?.isMultiSelectOn) {
        dispatch(
          selectValueRange({
            contentId,
            entryId: entryId,
            fieldId: fieldId,
          })
        );
      } else {
        dispatch(
          selectValueSingle({
            entryId: entryId,
            fieldId: fieldId,
          })
        );
        dispatch(setSoftSelection({ value: true }));
      }

      const reliability = ReliabilityMetrics.getInstance();
      reliability.successWithDuration(
        MetricName.DATABASE_SELECTION_CHANGE,
        startedRenderingAt,
        performance.now()
      );
    },
    [closeMenu, store, contentId, dispatch, entryId, fieldId]
  );

  const handleSelectValue = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      const multipleValuesSelected =
        selectSelectedValuesCount(store.getState(), {
          contentId,
        }) > 1;
      const isMultiSelectOn = !!e && isModifierKeyPressed(e, ['shiftKey']);
      if (!multipleValuesSelected) {
        onIsSelectedChange({ isMultiSelectOn });
      }
    },
    [contentId, onIsSelectedChange, store]
  );

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      handleSelectValue(e);
      const isSelected = selectIsValueSelected(store.getState(), {
        entryId,
        fieldId,
      });

      if (openThread?.valueId === getValueId(entryId, fieldId)) {
        return;
      }

      if (isSelected && isContentEditable) {
        e.preventDefault();
        toggleOpenMenu();
        blurActiveElement();
      }
    },
    [
      handleSelectValue,
      store,
      entryId,
      fieldId,
      openThread?.valueId,
      isContentEditable,
      toggleOpenMenu,
    ]
  );

  const triggerComponent = useCallback(
    (triggerProps?: TriggerProps) => (
      <TableLayoutCell
        contentId={contentId}
        entryId={entryId}
        fieldId={fieldId}
        isContentEditable={isContentEditable}
        canEditContent={canEditContent}
        canEditFields={canEditFields}
        currentViewId={currentViewId}
        isSelectionDisabled={isSelectionDisabled}
        onIsSelectedChange={onIsSelectedChange}
        handleOnContextMenu={handleOnContextMenu}
        {...tableLayoutCellProps}
        {...triggerProps}
        isFirstInRow={tableLayoutCellProps.isFirstInRow}
        ref={triggerProps?.ref}
      />
    ),
    [
      canEditContent,
      canEditFields,
      contentId,
      currentViewId,
      entryId,
      fieldId,
      handleOnContextMenu,
      isContentEditable,
      isSelectionDisabled,
      onIsSelectedChange,
      tableLayoutCellProps,
    ]
  );

  return (
    <SelectValueMenu
      scope={scope}
      Trigger={triggerComponent}
      contentId={contentId}
      currentViewId={currentViewId}
      canEditContent={canEditContent}
      canEditFields={canEditFields}
      entryId={entryId}
      fieldId={fieldId}
    />
  );
});

export default TableLayoutCellWithMenu;
